import { useId, useMemo } from 'react';
import { RED_BRIGHT } from '@/constants/colors';
import {
    Area,
    AreaChart as RechartsAreaChart,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    XAxis,
    YAxis,
} from 'recharts';
import {
    ChartTooltip,
    type ChartTooltipSource,
    type ChartTooltipSourceCount,
} from './ChartTooltip';

interface CustomTickProps {
    x?: number;
    y?: number;
    payload?: { value: string };
    formatter?: (value: string) => string;
}

function MultiLineXAxisTick({ x, y, payload, formatter }: CustomTickProps) {
    if (!payload?.value) return null;

    const formattedValue = formatter ? formatter(payload.value) : payload.value;
    const lines = formattedValue.split('\n');

    return (
        <g transform={`translate(${x},${y})`}>
            {lines.map((line, index) => (
                <text
                    key={index}
                    x={0}
                    y={index * 14}
                    dy={12}
                    textAnchor="middle"
                    fill="var(--grey-100)"
                    fontSize={12}
                >
                    {line}
                </text>
            ))}
        </g>
    );
}

export interface ChartDataPoint {
    date: string;
    value: number;
    startTime?: number;
    endTime?: number;
    sourceCounts?: ChartTooltipSourceCount[];
}

interface ActiveDotClickPayload {
    index?: number;
}

export interface ChartProps {
    data: ChartDataPoint[];
    dataKey?: string;
    strokeColor?: string;
    fillColor?: string;
    height?: number;
    showGrid?: boolean;
    yAxisDomain?: [number, number];
    yAxisTicks?: number[];
    gradientId?: string;
    margin?: { top?: number; right?: number; bottom?: number; left?: number };
    tooltipLabel?: string;
    tooltipSources?: ChartTooltipSource[];
    tooltipTitleFormatter?: (value: string) => string;
    onPointClick?: (dataPoint: ChartDataPoint, index: number) => void;
    xAxisTickFormatter?: (value: string) => string;
    xAxisInterval?:
        | number
        | 'preserveStart'
        | 'preserveEnd'
        | 'preserveStartEnd';
}

export function Chart({
    data,
    dataKey = 'value',
    strokeColor = RED_BRIGHT,
    fillColor = RED_BRIGHT,
    height = 298,
    showGrid = true,
    yAxisDomain,
    yAxisTicks,
    margin = { top: 10, right: 0, left: -20, bottom: 0 },
    gradientId,
    tooltipLabel = 'Events',
    tooltipSources,
    tooltipTitleFormatter,
    onPointClick,
    xAxisTickFormatter,
    xAxisInterval,
}: ChartProps) {
    const reactId = useId();
    const uniqueGradientId = gradientId || `gradient-${dataKey}-${reactId}`;

    const calculatedDomain = useMemo(() => {
        if (yAxisDomain) return yAxisDomain;
        const values = data.map((d) => d.value);
        const maxValue = Math.max(...values, 0);
        const niceMax = Math.ceil(maxValue / 5) * 5 || 5;
        return [0, niceMax] as [number, number];
    }, [data, yAxisDomain]);

    const calculatedTicks = useMemo(() => {
        if (yAxisTicks) return yAxisTicks;
        const [min, max] = calculatedDomain;
        const step = (max - min) / 5;
        return Array.from({ length: 6 }, (_, i) => min + step * i);
    }, [calculatedDomain, yAxisTicks]);

    const handleActiveDotClick = (
        _: unknown,
        payload: ActiveDotClickPayload,
    ) => {
        if (onPointClick && payload?.index !== undefined) {
            const point = data[payload.index];
            if (point) {
                onPointClick(point, payload.index);
            }
        }
    };

    return (
        <ResponsiveContainer width="100%" height={height}>
            <RechartsAreaChart data={data} margin={margin}>
                <defs>
                    <linearGradient
                        id={uniqueGradientId}
                        x1="0"
                        y1="0"
                        x2="0"
                        y2="1"
                    >
                        <stop
                            offset="0%"
                            stopColor={fillColor}
                            stopOpacity={0.4}
                        />
                        <stop
                            offset="100%"
                            stopColor={fillColor}
                            stopOpacity={0.05}
                        />
                    </linearGradient>
                </defs>

                {showGrid && (
                    <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="rgba(217, 217, 255, 0.25)"
                        vertical={false}
                    />
                )}

                <XAxis
                    dataKey="date"
                    stroke="var(--grey-100)"
                    tick={
                        xAxisTickFormatter ? (
                            <MultiLineXAxisTick
                                formatter={xAxisTickFormatter}
                            />
                        ) : (
                            { fill: 'var(--grey-100)', fontSize: 12 }
                        )
                    }
                    axisLine={{ stroke: 'var(--grey-500)' }}
                    tickLine={false}
                    tickMargin={4}
                    interval={xAxisInterval ?? 'preserveStartEnd'}
                />

                <YAxis
                    stroke="var(--grey-100)"
                    tick={{ fill: 'var(--grey-100)', fontSize: 12 }}
                    axisLine={false}
                    tickLine={false}
                    domain={calculatedDomain}
                    ticks={calculatedTicks}
                />

                <Tooltip
                    cursor={{
                        stroke: 'var(--grey-400)',
                        strokeDasharray: '3 3',
                    }}
                    content={
                        <ChartTooltip
                            valueLabel={tooltipLabel}
                            sources={tooltipSources}
                            titleFormatter={tooltipTitleFormatter}
                        />
                    }
                />

                <Area
                    type="linear"
                    dataKey={dataKey}
                    stroke={strokeColor}
                    strokeWidth={2}
                    fill={`url(#${uniqueGradientId})`}
                    fillOpacity={0.5}
                    dot={false}
                    activeDot={{
                        r: 6,
                        fill: strokeColor,
                        stroke: 'var(--grey-700)',
                        strokeWidth: 2,
                        cursor: onPointClick ? 'pointer' : 'default',
                        ...(onPointClick && { onClick: handleActiveDotClick }),
                    }}
                    animationDuration={700}
                    animationEasing="ease-out"
                    isAnimationActive={true}
                />
            </RechartsAreaChart>
        </ResponsiveContainer>
    );
}
